import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { apiRequest } from '../lib/api-client';
import { formatRelative } from '../ui/format';
import { Modal } from '../ui/Modal';
import { createDeal } from '../crm/api';
import { createTask } from '../work/api';
import { getSummary, suggestReply, summarise, type ThreadSummary } from './api';

/**
 * The four things an agent starts from the top bar (Phase 146).
 *
 * All four open a dialog rather than a screen, and the reason is the same for each: they happen
 * *during* a conversation. Sending somebody to the pipeline to raise a deal means leaving the
 * thread, losing the draft, and coming back to find the customer wrote again.
 *
 * The two that spend a model call — the summary and the sales suggestion — say so by only running
 * when a button is pressed. Nothing here calls a model on open.
 */

type Tool = 'deal' | 'task' | 'summary' | 'objection' | null;

export function ThreadTools({
  conversationId,
  contactId,
  contactName,
}: {
  conversationId: string;
  contactId: string | null;
  contactName: string;
  /** Puts a suggestion into the composer; the agent decides whether to send it. */
  onUseSuggestion?: (text: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState<Tool>(null);

  return (
    <>
      <div className="thread__tools">
        <ToolButton label={t('inbox.createDeal')} onClick={() => setOpen('deal')}>
          <DealIcon />
        </ToolButton>

        <ToolButton label={t('inbox.createTask')} onClick={() => setOpen('task')}>
          <TaskIcon />
        </ToolButton>

        <ToolButton label={t('inbox.summary')} onClick={() => setOpen('summary')}>
          <SummaryIcon />
        </ToolButton>

        <ToolButton label={t('inbox.objection')} onClick={() => setOpen('objection')}>
          <SalesIcon />
        </ToolButton>
      </div>

      <DealModal
        open={open === 'deal'}
        contactId={contactId}
        contactName={contactName}
        onClose={() => setOpen(null)}
      />

      <TaskModal
        open={open === 'task'}
        contactId={contactId}
        contactName={contactName}
        onClose={() => setOpen(null)}
      />

      <SummaryModal
        open={open === 'summary'}
        conversationId={conversationId}
        onClose={() => setOpen(null)}
      />

      <ObjectionModal
        open={open === 'objection'}
        conversationId={conversationId}
        onClose={() => setOpen(null)}
      />
    </>
  );
}

function ToolButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="button button--quiet button--compact"
      onClick={onClick}
      title={label}
    >
      {children}
      <span className="thread__tool-label">{label}</span>
    </button>
  );
}

/* ----- a deal, raised from the conversation ----- */

function DealModal({
  open,
  contactId,
  contactName,
  onClose,
}: {
  open: boolean;
  contactId: string | null;
  contactName: string;
  onClose: () => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  const [title, setTitle] = useState(contactName);
  const [amount, setAmount] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pipelines = useQuery({
    queryKey: ['pipelines'],
    queryFn: () =>
      apiRequest<{
        pipelines: {
          id: string;
          name: string;
          is_default: boolean;
          stages: { id: string; name: string; position: number }[];
        }[];
      }>('/v1/crm/pipelines'),
    enabled: open,
  });

  const list = pipelines.data?.pipelines ?? [];
  const pipeline =
    list.find((each) => each.id === pipelineId) ?? list.find((each) => each.is_default) ?? list[0];
  const stages = [...(pipeline?.stages ?? [])].sort((a, b) => a.position - b.position);
  const stage = stages.find((each) => each.id === stageId) ?? stages[0];

  const create = useMutation({
    mutationFn: () => {
      if (!pipeline || !stage) throw new Error(t('inbox.noPipeline'));

      return createDeal({
        title: title.trim(),
        pipelineId: pipeline.id,
        stageId: stage.id,
        // Reais typed, centavos stored: the amount is money and money is never a float here.
        ...(amount.trim()
          ? { amountCents: Math.round(Number(amount.replace(',', '.')) * 100) }
          : {}),
        ...(contactId ? { contactId } : {}),
      } as Parameters<typeof createDeal>[0]);
    },
    onSuccess: (created) => {
      onClose();
      navigate(`/crm/registro/opportunities/${created.id}`);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Modal
      open={open}
      title={t('inbox.createDeal')}
      description={t('inbox.createDealHint').replace('{name}', contactName)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button button--quiet" onClick={onClose}>
            {t('confirm.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={title.trim() === '' || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? t('work.creating') : t('inbox.createDeal')}
          </button>
        </>
      }
    >
      <label className="stack__field">
        <span>{t('inbox.dealTitle')}</span>
        <input
          className="input"
          autoFocus
          maxLength={200}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>

      <label className="stack__field">
        <span>{t('inbox.dealAmount')}</span>
        <input
          className="input"
          inputMode="decimal"
          placeholder="0,00"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </label>

      <div className="service-hours__window">
        <label className="stack__field">
          <span>{t('inbox.dealPipeline')}</span>
          <select
            className="select"
            value={pipeline?.id ?? ''}
            onChange={(event) => {
              setPipelineId(event.target.value);
              setStageId('');
            }}
          >
            {list.map((each) => (
              <option key={each.id} value={each.id}>
                {each.name}
              </option>
            ))}
          </select>
        </label>

        <label className="stack__field">
          <span>{t('inbox.dealStage')}</span>
          <select
            className="select"
            value={stage?.id ?? ''}
            onChange={(event) => setStageId(event.target.value)}
          >
            {stages.map((each) => (
              <option key={each.id} value={each.id}>
                {each.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/* ----- a task, with the customer on it ----- */

function TaskModal({
  open,
  contactId,
  contactName,
  onClose,
}: {
  open: boolean;
  contactId: string | null;
  contactName: string;
  onClose: () => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'>('MEDIUM');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      createTask({
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        // A date in the box, an instant on the wire: the server stores UTC and the tenant reads it
        // in its own timezone.
        ...(dueDate ? { dueDate: new Date(dueDate).toISOString() } : {}),
        priority,
        ...(contactId ? { related: { type: 'CONTACT' as const, id: contactId } } : {}),
      }),
    onSuccess: () => {
      setDone(true);
      setTitle('');
      setDescription('');
      setDueDate('');
      void client.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Modal
      open={open}
      title={t('inbox.createTask')}
      description={t('inbox.createTaskHint').replace('{name}', contactName)}
      onClose={() => {
        setDone(false);
        onClose();
      }}
      footer={
        <>
          <button
            type="button"
            className="button button--quiet"
            onClick={() => {
              setDone(false);
              onClose();
            }}
          >
            {done ? t('common.close') : t('confirm.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={title.trim() === '' || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? t('work.creating') : t('inbox.createTask')}
          </button>
        </>
      }
    >
      <label className="stack__field">
        <span>{t('inbox.taskTitle')}</span>
        <input
          className="input"
          autoFocus
          maxLength={200}
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
            setDone(false);
          }}
        />
      </label>

      <label className="stack__field">
        <span>{t('inbox.taskNotes')}</span>
        <textarea
          className="input"
          rows={3}
          maxLength={2000}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <div className="service-hours__window">
        <label className="stack__field">
          <span>{t('inbox.taskDue')}</span>
          <input
            className="input"
            type="datetime-local"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('inbox.priority')}</span>
          <select
            className="select"
            value={priority}
            onChange={(event) => setPriority(event.target.value as typeof priority)}
          >
            {(['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const).map((each) => (
              <option key={each} value={each}>
                {t(`work.priority.${each}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {done && <p className="muted small">{t('inbox.taskCreated')}</p>}

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/* ----- what this conversation is about ----- */

function SummaryModal({
  open,
  conversationId,
  onClose,
}: {
  open: boolean;
  conversationId: string;
  onClose: () => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ['conversation-summary', conversationId],
    queryFn: () => getSummary(conversationId),
    enabled: open,
    // A plan without AI has no summary, not a broken thread.
    retry: false,
  });

  const write = useMutation({
    mutationFn: () => summarise(conversationId),
    onSuccess: (written: ThreadSummary) => {
      setError(null);
      client.setQueryData(['conversation-summary', conversationId], written);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const current = summary.data;

  return (
    <Modal
      open={open}
      title={t('inbox.summary')}
      description={t('inbox.summaryHint')}
      onClose={onClose}
      wide
      footer={
        <>
          {/* When it was last written, beside the button that writes it again. */}
          {current?.generatedAt && (
            <span className="muted small thread__tools-when">
              {t('inbox.summaryWhen').replace('{when}', formatRelative(current.generatedAt))}
            </span>
          )}

          <button type="button" className="button button--quiet" onClick={onClose}>
            {t('common.close')}
          </button>

          <button
            type="button"
            className="button button--primary"
            disabled={write.isPending}
            onClick={() => write.mutate()}
          >
            {write.isPending
              ? t('inbox.summarising')
              : current?.text
                ? t('inbox.summaryRefresh')
                : t('inbox.summarise')}
          </button>
        </>
      }
    >
      {summary.isError && <p className="muted">{t('inbox.summaryUnavailable')}</p>}

      {current?.text ? (
        <>
          {current.stale && <p className="muted small">{t('inbox.summaryStale')}</p>}
          <p className="modal__text">{current.text}</p>
        </>
      ) : (
        !summary.isError && <p className="muted">{t('inbox.summaryEmpty')}</p>
      )}

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/* ----- the sales assistant ----- */

function ObjectionModal({
  open,
  conversationId,
  onClose,
}: {
  open: boolean;
  conversationId: string;
  onClose: () => void;
}): React.JSX.Element {
  const [window, setWindow] = useState(10);
  const [suggestion, setSuggestion] = useState<{
    text: string;
    sources: { id: string; title: string }[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const ask = useMutation({
    mutationFn: () => suggestReply(conversationId, window),
    onSuccess: (result) => {
      setError(null);
      setCopied(false);
      setSuggestion({ text: result.text, sources: result.sources });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Modal
      open={open}
      title={t('inbox.objection')}
      description={t('inbox.objectionHint')}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="button button--quiet" onClick={onClose}>
            {t('common.close')}
          </button>

          {suggestion && (
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                void navigator.clipboard?.writeText(suggestion.text);
                setCopied(true);
              }}
            >
              {copied ? t('files.shareCopied') : t('inbox.copySuggestion')}
            </button>
          )}

          <button
            type="button"
            className="button button--primary"
            disabled={ask.isPending}
            onClick={() => ask.mutate()}
          >
            {ask.isPending
              ? t('inbox.suggesting')
              : suggestion
                ? t('inbox.suggestAgain')
                : t('inbox.suggest')}
          </button>
        </>
      }
    >
      <div className="stack__field">
        <span>{t('inbox.objectionWindow')}</span>
        <div className="chips">
          {[5, 10, 30, 50].map((size) => (
            <button
              key={size}
              type="button"
              className={`chip${window === size ? ' chip--active' : ''}`}
              aria-pressed={window === size}
              onClick={() => setWindow(size)}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {suggestion ? (
        <>
          <p className="modal__text">{suggestion.text}</p>

          {/*
           * Which articles it leaned on.
           *
           * Named because the suggestion is about to be sent to a customer in the tenant's name:
           * an agent who can see where a claim came from can check it, and one who cannot has to
           * trust a model about their own company's prices.
           */}
          {suggestion.sources.length > 0 ? (
            <p className="muted small">
              {t('inbox.objectionSources')}{' '}
              {suggestion.sources.map((source) => source.title).join(', ')}
            </p>
          ) : (
            <p className="muted small">{t('inbox.objectionNoSources')}</p>
          )}
        </>
      ) : (
        <p className="muted">{t('inbox.objectionEmpty')}</p>
      )}

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/* ----- the icons ----- */

function DealIcon(): React.JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M3 7h18v12H3z" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function TaskIcon(): React.JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M8 3h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function SummaryIcon(): React.JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M4 6h16M4 12h10M4 18h7" />
    </svg>
  );
}

function SalesIcon(): React.JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M12 3l2.1 4.6 5 .6-3.7 3.4 1 4.9L12 14l-4.4 2.5 1-4.9L4.9 8.2l5-.6z" />
      <path d="M5 21h14" />
    </svg>
  );
}
